/** @forge-header
 * @id          cfbParser
 * @role        College Football ticker parser | KXNCAAFGAME game moneylines
 * @pattern     pure-fn
 * @purity      none
 * @units       n/a
 * @upstream    kalshiTicker/types:ParsedTicker,GameInfo | kalshiTicker/helpers:parseFallback,splitCompetitors
 * @downstream  kalshiTicker/parse
 * @exports     parseCfbGame:fn
 * @test        parse.test.ts (indirect)
 */

import type { ParsedTicker, GameInfo } from '../types';
import { parseDateFromTicker } from '../types';
import { parseFallback, splitCompetitors } from '../helpers';

/**
 * College Football: KXNCAAFGAME-26SEP19MSUND-ND
 * Team-vs-team moneyline with variable-length team codes — uses the outcome
 * code to anchor the away/home split. No team-name registry (codes shown
 * as-is), like NCAA basketball.
 */
export function parseCfbGame(raw: string): ParsedTicker {
  const parts = raw.split('-');
  if (parts.length < 2) return parseFallback(raw);

  const mid = parts[1] ?? '';
  if (mid.length < 9) return parseFallback(raw); // 7 date + >=1+1 codes

  const date = parseDateFromTicker(mid.slice(0, 7));
  const outcome = parts[2] ?? null;
  const [awayCode, homeCode] = splitCompetitors(mid.slice(7), outcome);

  const game: GameInfo = { awayCode, homeCode };
  const shortLabel = `CFB: ${awayCode} @ ${homeCode}`;
  const fullLabel = outcome
    ? `CFB: ${awayCode} @ ${homeCode} · ${outcome} Win`
    : `CFB: ${awayCode} @ ${homeCode}`;

  return {
    raw,
    category: 'cfb-game',
    series: 'KXNCAAFGAME',
    date,
    game,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    outcome,
    shortLabel,
    fullLabel,
  };
}
